<template>
	<div class="relative flex flex-col min-h-dvh-safe lg:overflow-x-hidden">
		<div class="hidden-print sticky z-20 flex h-16 flex-shrink-0 items-center bg-primary shadow-lg top-safe">
			<div class="hidden w-64 items-center self-stretch px-6 lg:flex">
				<NuxtLink :to="$routeUrl.HomeUrl()" class="h-10">
					<img src="/img/app_logo_white.png" class="text-white h-10 w-auto" alt="" />
				</NuxtLink>
			</div>
			<div class="bg-white flex flex-grow items-center self-stretch bg-opacity-10">
				<div
					class="flex h-16 cursor-pointer items-center justify-center px-6 duration-300 lg:hidden"
					:class="state.isMenuOpen ? 'bg-black' : ''"
					@click="state.isMenuOpen = !state.isMenuOpen"
				>
					<div class="flex h-16 w-auto scale-75 transform flex-col items-center justify-center duration-300">
						<span
							class="bg-white mb-3 h-1 w-10 transform rounded duration-300"
							:class="state.isMenuOpen ? 'translate-y-4 rotate-45' : ''"
						/>
						<span
							class="bg-white mb-3 h-1 w-10 transform rounded duration-300"
							:class="state.isMenuOpen ? 'opacity-0' : 'opacity-100'"
						/>
						<span
							class="bg-white h-1 w-10 transform rounded duration-300"
							:class="state.isMenuOpen ? '-translate-y-4 -rotate-45' : ''"
						/>
					</div>
				</div>
				<span class="text-white ms-6 flex items-center text-lg font-bold">
					<span>
						{{ $t("management") }}
					</span>
					<span class="bg-white ms-2 rounded-md bg-opacity-90 px-2 py-0.5 text-xs text-primary">
						{{ userTypeName }}
					</span>
				</span>
				<div class="flex-grow" />
				<Icon
					name="mdi:logout"
					class="text-white me-6 h-8 w-8 transform cursor-pointer text-3xl duration-300 hover:scale-90"
					@click="logout"
				/>
			</div>
		</div>
		<div class="relative flex flex-shrink-0 flex-grow flex-row">
			<div
				class="hidden-print bg-gray-200 fixed z-[12] w-64 flex-shrink-0 transform overflow-y-auto overscroll-contain shadow-xl duration-300 h-dvh-safe lg:relative lg:h-auto lg:shadow-lg"
				:class="
					state.isMenuOpen
						? 'translate-x-0 opacity-100'
						: '-translate-x-64 opacity-0 lg:translate-x-0 lg:opacity-100'
				"
			>
				<div class="flex flex-col py-5">
					<NuxtLink
						v-for="(item, i) in menuItems"
						:key="i"
						:to="item.to"
						class="flex items-center px-6 py-5 duration-300"
						:class="{
							'pointer-events-none bg-primary bg-opacity-10 font-medium text-primary':
								selected === item.id,
							'text-neutral-700 hover:text-neutral-800 hover:bg-gray-400 hover:bg-opacity-10':
								selected !== item.id
						}"
					>
						<Icon :name="item.icon" class="me-6 h-6 w-6" />
						<span class="text-sm">{{ item.title }}</span>
						<div class="flex-grow" />
						<span v-if="item.yellow" class="bg-yellow-500 h-3 w-3 animate-bounce rounded-full shadow-md" />
					</NuxtLink>
					<div class="flex-grow"></div>
					<div class="border-slate-300 mx-6 -mb-2 mt-5 flex flex-col items-center border-t pt-3">
						<BaseLanguageSelector />
					</div>
				</div>
			</div>
			<div class="bg-gray-50 relative flex flex-grow flex-col overflow-x-hidden">
				<slot v-if="state.preFetched && fetched"></slot>
				<div
					v-if="loading || state.loading"
					class="bg-white fixed z-10 flex items-center justify-center bg-opacity-40 top-safe right-safe left-safe h-dvh-safe"
				>
					<span class="h-12 w-12 animate-ping rounded-full bg-primary" />
				</div>
				<div
					v-else-if="state.preFetched ? !fetched : !state.preFetched"
					class="bg-white fixed z-10 flex items-center justify-center bg-opacity-40 top-safe right-safe left-safe h-dvh-safe"
				>
					<span
						class="ripple-bg-yellow-500 text-white cursor-pointer rounded-lg px-6 py-3 font-bold shadow-lg"
						@click="state.preFetched ? emit('tryAgain') : preFetch()"
					>
						{{ $t("try_again") }}
					</span>
				</div>
			</div>
		</div>
		<div
			v-if="state.isMenuOpen"
			class="hidden-print bg-black absolute bottom-0 left-0 right-0 top-0 z-[11] bg-opacity-30"
			@click="state.isMenuOpen = false"
			@wheel.prevent
			@touchmove.prevent
			@scroll.prevent
		/>
	</div>
</template>

<script setup lang="ts">
import type { AdminMenuItemIds } from "~/stores/panel"

const app = useNuxtApp()
const userStore = useUserStore()
const panelStore = usePanelStore()
const { $i18n, $routeUrl, $toastErrors } = useNuxtApp()

const emit = defineEmits<{
	tryAgain: []
}>()

const props = withDefaults(
	defineProps<{
		selected?: AdminMenuItemIds
		loading?: boolean
		fetched?: boolean
		error?: Error | null
	}>(),
	{
		fetched: true
	}
)

const state = reactive({
	isMenuOpen: false,
	loading: true,
	preFetched: false
})

const user = computed(() => {
	return userStore.user
})
const menuItems = computed(() =>
	panelStore.adminMenuItems.filter((item) => item.roles.includes(user.value?.type || ""))
)

const selectedMenuItem = computed(() => panelStore.adminMenuItems.find((v) => v.id === props.selected) || null)

const typeList = computed(() => {
	return userStore.types
})
const userTypeName = computed(() => {
	return typeList.value.find((v) => v.value === user.value?.type)?.name || $i18n.t("admin")
})

const logout = async () => {
	state.loading = true
	const [, err] = await userStore.logout()
	if (err) app.$toastErrors(err)
	state.loading = false
}

const preFetch = () => {
	state.preFetched = false
	state.loading = true
	try {
		state.preFetched = true
	} catch (e: any) {
		$toastErrors(e)
	}
	state.loading = false
}

watch(
	() => props.error,
	() => props.error && $toastErrors(props.error)
)

onMounted(() => {
	props.error && $toastErrors(props.error)
	preFetch()
})

useHead({
	title: selectedMenuItem.value
		? selectedMenuItem.value?.title + " | " + $i18n.t("manager_panel")
		: $i18n.t("manager_panel")
})
</script>
